import type { Directive } from "vue";

type AutoResizeOptions = { minHeight?: number } | undefined;

/**
 * `v-auto-resize` — grow a ``<textarea>`` to fit its content.
 *
 * Usage::
 *
 *     <textarea v-auto-resize v-model="value" />
 *     <textarea v-auto-resize="{ minHeight: 60 }" v-model="value" />
 *
 * Implementation notes:
 * - Initial measurement is deferred with ``requestAnimationFrame`` because
 *   ``scrollHeight`` is 0 before the browser has laid out the element.
 * - The ``updated`` hook runs after every re-render that touches this node,
 *   which covers both user typing (via ``v-model``) and external assignment
 *   to the bound value, so there is no need for an explicit ``input``
 *   listener or a ``nextTick`` dance in the host component.
 */
let autoResizePauseDepth = 0;

/** Suppress ``v-auto-resize`` measurement while Sortable is dragging (avoids row jumping). */
export function pauseAutoResize(): void {
  autoResizePauseDepth += 1;
}

export function resumeAutoResize(): void {
  autoResizePauseDepth = Math.max(0, autoResizePauseDepth - 1);
}

function isAutoResizePaused(): boolean {
  return autoResizePauseDepth > 0;
}

function resize(el: HTMLTextAreaElement, value: AutoResizeOptions): void {
  const minHeight = value?.minHeight ?? 0;
  el.style.height = "0";
  el.style.height = `${Math.max(el.scrollHeight, minHeight)}px`;
}

export const vAutoResize: Directive<HTMLTextAreaElement, AutoResizeOptions> = {
  mounted(el, binding) {
    requestAnimationFrame(() => {
      if (isAutoResizePaused()) return;
      resize(el, binding.value);
    });
  },
  updated(el, binding) {
    if (isAutoResizePaused()) return;
    resize(el, binding.value);
  },
};
